import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { formatPoints } from "@/lib/format";
import { Check, X, FileUp, Image as ImageIcon, Eye } from "lucide-react";

export default function AdminWithdrawalsTab({ onChange }) {
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState("pending");
    const [notes, setNotes] = useState("");
    const [payTarget, setPayTarget] = useState(null);

    const load = useCallback(async () => {
        const { data } = await api.get("/admin/withdrawals", { params: { status: filter || undefined } });
        setItems(data);
    }, [filter]);

    useEffect(() => { load(); }, [load]);

    const decide = async (w, action) => {
        if (action === "mark-paid") {
            setPayTarget(w);
            return;
        }
        try {
            await api.post(`/admin/withdrawals/${w.id}/${action}`, { notes });
            toast.success(action === "approve" ? "Retiro aprobado" : "Retiro rechazado");
            setNotes("");
            await load();
            onChange?.();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
        }
    };

    const submitPayment = async (proofUrl, paidNotes) => {
        try {
            await api.post(`/admin/withdrawals/${payTarget.id}/mark-paid`, {
                notes: paidNotes,
                payment_proof_url: proofUrl,
            });
            toast.success("Retiro marcado como pagado");
            setPayTarget(null);
            await load();
            onChange?.();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
        }
    };

    const labels = { pending: "Pendientes", on_hold: "En revisión", approved: "Aprobados", paid: "Pagados", rejected: "Rechazados" };

    return (
        <div>
            <div className="flex flex-wrap items-center gap-2 mb-4">
                {["pending", "on_hold", "approved", "paid", "rejected", ""].map((s) => (
                    <button
                        key={s || "all"}
                        onClick={() => setFilter(s)}
                        data-testid={`withdrawal-filter-${s || "all"}`}
                        className={`px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider border transition ${
                            filter === s
                                ? "bg-p2w-green text-p2w-green-fg border-p2w-green"
                                : "bg-p2w-surface text-white/80 border-p2w-line hover:border-p2w-green/40"
                        }`}
                    >
                        {s ? labels[s] : "Todos"}
                    </button>
                ))}
            </div>

            {items.length === 0 ? (
                <div className="rounded-2xl border border-p2w-line bg-p2w-surface p-10 text-center text-p2w-gray">
                    No hay solicitudes en esta categoría.
                </div>
            ) : (
                <div className="space-y-3">
                    {items.map((w) => (
                        <WithdrawalCard key={w.id} w={w} notes={notes} setNotes={setNotes} decide={decide} />
                    ))}
                </div>
            )}

            <MarkPaidDialog target={payTarget} onClose={() => setPayTarget(null)} onSubmit={submitPayment} />
        </div>
    );
}

function MarkPaidDialog({ target, onClose, onSubmit }) {
    const [file, setFile] = useState(null);
    const [dataUrl, setDataUrl] = useState("");
    const [paidNotes, setPaidNotes] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!target) { setFile(null); setDataUrl(""); setPaidNotes(""); }
    }, [target]);

    const onFile = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (f.size > 5 * 1024 * 1024) { toast.error("Máx 5MB"); return; }
        setFile(f);
        const r = new FileReader();
        r.onload = (ev) => setDataUrl(ev.target.result);
        r.readAsDataURL(f);
    };

    const submit = async () => {
        if (!dataUrl) { toast.error("Sube el comprobante del envío"); return; }
        setSubmitting(true);
        try { await onSubmit(dataUrl, paidNotes); }
        finally { setSubmitting(false); }
    };

    return (
        <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="bg-p2w-surface border-p2w-line text-white max-w-md">
                <DialogHeader>
                    <DialogTitle className="font-sora">Marcar como pagado</DialogTitle>
                </DialogHeader>
                {target && (
                    <div className="space-y-4">
                        <div className="rounded-xl border border-p2w-line bg-p2w-elevated px-4 py-3 text-sm">
                            <div className="flex items-center justify-between">
                                <span className="text-p2w-gray">Usuario</span>
                                <span className="text-white">@{target.username}</span>
                            </div>
                            <div className="flex items-center justify-between mt-1">
                                <span className="text-p2w-gray">Neto a pagar</span>
                                <span className="font-sora font-bold text-p2w-green number-tabular">{formatPoints(target.net_amount || target.amount)} pts</span>
                            </div>
                        </div>
                        <div>
                            <label className="text-xs uppercase tracking-widest text-p2w-gray">Comprobante del envío <span className="text-p2w-amber">*</span></label>
                            <label className="mt-2 flex items-center gap-3 rounded-xl border border-dashed border-p2w-line bg-p2w-elevated px-4 py-3 cursor-pointer hover:border-p2w-green/40">
                                <FileUp className="w-5 h-5 text-p2w-green" />
                                <span className="text-sm text-white/80 truncate">{file ? file.name : "Toca para adjuntar imagen o PDF"}</span>
                                <input type="file" accept="image/*,application/pdf" hidden onChange={onFile} data-testid="payment-proof-file" />
                            </label>
                            {dataUrl && dataUrl.startsWith("data:image") && (
                                <img alt="Comprobante" src={dataUrl} className="mt-2 w-full max-h-44 object-contain rounded-lg border border-p2w-line bg-p2w-bg" />
                            )}
                        </div>
                        <div>
                            <label className="text-xs uppercase tracking-widest text-p2w-gray">Notas (opcional)</label>
                            <Textarea value={paidNotes} onChange={(e) => setPaidNotes(e.target.value)} className="mt-2 bg-p2w-elevated border-p2w-line text-white text-sm" placeholder="Ej. Bre-B referencia 9821..." />
                        </div>
                    </div>
                )}
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} className="border-p2w-line bg-transparent text-white">Cancelar</Button>
                    <Button onClick={submit} disabled={submitting || !dataUrl} data-testid="confirm-mark-paid" className="bg-p2w-blue text-white hover:bg-p2w-blue/90 font-semibold">
                        {submitting ? "Enviando…" : "Confirmar pago"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function WithdrawalCard({ w, notes, setNotes, decide }) {
    const canReview = w.status === "pending" || w.status === "on_hold";
    return (
        <div data-testid={`withdrawal-${w.id}`} className="rounded-2xl border border-p2w-line bg-p2w-surface p-5">
            <div className="flex items-start justify-between flex-wrap gap-4">
                <div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-sora font-bold text-2xl text-p2w-green number-tabular">
                            {formatPoints(w.amount)} pts
                        </span>
                        <StatusBadge status={w.status} />
                        {w.withholding > 0 && (
                            <Badge className="bg-p2w-amber/15 text-p2w-amber border-p2w-amber/40 text-[10px] uppercase tracking-wider border">
                                Retención {formatPoints(w.withholding)}
                            </Badge>
                        )}
                    </div>
                    <div className="mt-2 text-sm text-white">
                        <span className="text-p2w-gray">Usuario:</span> @{w.username}
                    </div>
                    {w.holder_name && (
                        <div className="mt-1 text-sm text-white">
                            <span className="text-p2w-gray">Titular cuenta:</span> {w.holder_name}
                        </div>
                    )}
                    <div className="mt-1 text-sm">
                        <span className="text-p2w-gray">Destino:</span>{" "}
                        <code className="text-xs bg-p2w-elevated border border-p2w-line px-2 py-0.5 rounded">{w.destination}</code>
                        {w.method && <span className="ml-2 text-[10px] uppercase tracking-wider text-p2w-gray">{w.method === "bre_b" ? "Bre-B" : w.method === "crypto" ? "USDT TRC20" : w.method}</span>}
                    </div>
                    {w.net_amount !== undefined && w.net_amount !== w.amount && (
                        <div className="mt-1 text-sm">
                            <span className="text-p2w-gray">Neto a pagar:</span>{" "}
                            <span className="font-sora font-bold text-p2w-green number-tabular">{formatPoints(w.net_amount)} pts</span>
                        </div>
                    )}
                    {w.status === "on_hold" && (
                        <div className="mt-2 text-xs text-p2w-amber bg-p2w-amber/10 border border-p2w-amber/30 rounded-md px-2.5 py-1.5 inline-block">
                            ⚠ En revisión manual: el titular de la cuenta no coincide con el perfil del usuario.
                        </div>
                    )}
                    {w.notes && <div className="mt-1 text-xs text-p2w-gray">"{w.notes}"</div>}
                    <div className="mt-1 text-xs text-p2w-gray">Solicitado: {new Date(w.requested_at).toLocaleString()}</div>
                </div>
                {canReview && (
                    <div className="flex flex-col gap-2 min-w-[260px]">
                        <Textarea
                            placeholder="Notas (opcional)"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            className="bg-p2w-elevated border-p2w-line text-white text-sm min-h-[60px] focus-visible:ring-p2w-green/40"
                        />
                        <div className="flex gap-2">
                            <Button size="sm" data-testid={`approve-withdrawal-${w.id}`} onClick={() => decide(w, "approve")} className="flex-1 bg-p2w-green text-p2w-green-fg hover:bg-p2w-green/90 font-semibold">
                                <Check className="w-4 h-4 mr-1" /> Aprobar
                            </Button>
                            <Button size="sm" variant="outline" data-testid={`reject-withdrawal-${w.id}`} onClick={() => decide(w, "reject")} className="flex-1 border-p2w-red/40 bg-p2w-red/10 text-p2w-red hover:bg-p2w-red/20 hover:text-p2w-red">
                                <X className="w-4 h-4 mr-1" /> Rechazar
                            </Button>
                        </div>
                    </div>
                )}
                {w.status === "approved" && (
                    <div className="flex flex-col gap-2 min-w-[180px]">
                        <Button size="sm" data-testid={`mark-paid-${w.id}`} onClick={() => decide(w, "mark-paid")} className="bg-p2w-blue text-white hover:bg-p2w-blue/90 font-semibold">
                            Marcar como pagado
                        </Button>
                    </div>
                )}
                {w.status === "paid" && w.payment_proof_url && (
                    <div className="flex flex-col gap-2 min-w-[180px]">
                        <a
                            href={w.payment_proof_url}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`view-proof-${w.id}`}
                            className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-p2w-blue/40 bg-p2w-blue/10 text-p2w-blue text-xs font-semibold hover:bg-p2w-blue/20 transition"
                        >
                            <Eye className="w-3.5 h-3.5" /> Ver comprobante
                        </a>
                        {w.paid_at && (
                            <div className="text-[10px] text-p2w-gray text-center">
                                Pagado: {new Date(w.paid_at).toLocaleString()}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

function StatusBadge({ status }) {
    const map = {
        pending: "bg-p2w-amber/15 text-p2w-amber border-p2w-amber/40",
        on_hold: "bg-p2w-amber/15 text-p2w-amber border-p2w-amber/40",
        approved: "bg-p2w-green/15 text-p2w-green border-p2w-green/40",
        paid: "bg-p2w-blue/15 text-p2w-blue border-p2w-blue/40",
        rejected: "bg-p2w-red/15 text-p2w-red border-p2w-red/40",
    };
    const labels = { pending: "Pendiente", on_hold: "En revisión", approved: "Aprobado", paid: "Pagado", rejected: "Rechazado" };
    return (
        <Badge className={`${map[status] || map.pending} text-[10px] uppercase tracking-wider border`}>
            {labels[status] || status}
        </Badge>
    );
}
